// Issue #363 — shared contract for the admin-editable company profile and the
// Markdown legal pages (service framework, cancellation/refund, terms of use).
// Storage: app_settings keys COMPANY_PROFILE_SETTING_KEY and
// legalPageSettingKey(slug). Public reads never require auth; writes are admin-only.

export const COMPANY_PROFILE_SETTING_KEY = 'company_profile';

export interface CompanyProfile {
  legalName: string;
  address: string;
  email: string;
  phone: string;
  whatsapp: string;
  taxOffice: string;
  taxNumber: string;
}

export type CompanyProfileField = keyof CompanyProfile;

export const COMPANY_PROFILE_FIELDS: readonly CompanyProfileField[] = [
  'legalName',
  'address',
  'email',
  'phone',
  'whatsapp',
  'taxOffice',
  'taxNumber',
] as const;

/** Seed values used when no stored profile exists (owner-confirmed 2026-10-21). */
export const COMPANY_PROFILE_DEFAULTS: CompanyProfile = {
  legalName: 'Muhammed Bilal Benli / MBBE SOFTWARE',
  address: 'Fenerbahçe Mah. İğrip Sk. No: 13 İç Kapı No: 1 Kadıköy / İstanbul',
  email: 'info@mbbe.dev',
  phone: '+90 850 308 30 86',
  whatsapp: '+90 850 308 30 86',
  taxOffice: '',
  taxNumber: '',
};

export const COMPANY_PROFILE_MAX_LENGTH = 300;

export const LEGAL_PAGE_SLUGS = ['hizmet-sozlesmesi', 'iptal-iade-politikasi', 'kullanim-kosullari'] as const;
export type LegalPageSlug = (typeof LEGAL_PAGE_SLUGS)[number];

export type LegalPageLocale = 'tr' | 'en';
export const LEGAL_PAGE_LOCALES: readonly LegalPageLocale[] = ['tr', 'en'] as const;

export const LEGAL_PAGE_MAX_MARKDOWN_LENGTH = 100_000;

export function legalPageSettingKey(slug: LegalPageSlug): string {
  return `legal_page:${slug}`;
}

export interface LegalPageLocaleContent {
  title: string;
  markdown: string;
}

/** Stored value of app_settings[legalPageSettingKey(slug)]. Empty strings mean "use default". */
export interface LegalPageContent {
  tr: LegalPageLocaleContent;
  en: LegalPageLocaleContent;
}

/** GET /api/site/profile (public) */
export interface PublicCompanyProfileResponse {
  profile: Partial<CompanyProfile>; // empty fields omitted
  updatedAt: string | null;
}

/** GET /api/site/legal-pages/:slug (public). Server applies defaults + EN→TR fallback; placeholders unresolved. */
export interface PublicLegalPageResponse {
  slug: LegalPageSlug;
  tr: LegalPageLocaleContent;
  en: LegalPageLocaleContent;
  updatedAt: string | null;
  isDefault: boolean;
}

/**
 * Admin endpoints (admin auth, fail-closed, audited):
 *   GET  /api/admin/site/profile            -> { profile: CompanyProfile; updatedAt: string | null }
 *   PUT  /api/admin/site/profile            body CompanyProfile -> same as GET
 *   GET  /api/admin/site/legal-pages/:slug  -> { slug; content: LegalPageContent; defaults: LegalPageContent; updatedAt: string | null }
 *   PUT  /api/admin/site/legal-pages/:slug  body LegalPageContent -> same as GET
 */
export interface AdminCompanyProfileResponse {
  profile: CompanyProfile;
  updatedAt: string | null;
}

export interface AdminLegalPageResponse {
  slug: LegalPageSlug;
  content: LegalPageContent;
  defaults: LegalPageContent;
  updatedAt: string | null;
}

/** Placeholders usable in legal Markdown, resolved from CompanyProfile at render time. */
export const LEGAL_PLACEHOLDER_PATTERN = /\{\{(legalName|address|email|phone|whatsapp|taxOffice|taxNumber)\}\}/g;

/**
 * Resolve placeholders. Any line that references an empty/missing field is dropped entirely,
 * so e.g. "Vergi Dairesi: {{taxOffice}}" disappears until the owner fills it in.
 */
export function resolveLegalPlaceholders(markdown: string, profile: Partial<CompanyProfile>): string {
  return markdown
    .split('\n')
    .filter((line) => {
      const fields = [...line.matchAll(LEGAL_PLACEHOLDER_PATTERN)].map((m) => m[1] as CompanyProfileField);
      return fields.every((f) => (profile[f] ?? '').trim().length > 0);
    })
    .map((line) => line.replace(LEGAL_PLACEHOLDER_PATTERN, (_, f: CompanyProfileField) => (profile[f] ?? '').trim()))
    .join('\n');
}

export { LEGAL_PAGE_DEFAULTS } from './legal-page-defaults';
